import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  TrendingUp, 
  Users, 
  Target,
  Calendar,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsagePage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API}/usage/extractions`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error("Failed to fetch usage:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const myUsage = usage?.my_usage || { used: 0, limit: 10, remaining: 10 };
  const teamUsage = usage?.team_usage || [];
  const usagePercent = (myUsage.used / myUsage.limit) * 100;
  const isNearLimit = usagePercent >= 80;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="usage-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Usage & Limits
        </h1>
        <p className="text-slate-500 mt-1">
          Track your daily lead extraction usage
        </p>
      </div>

      {/* My Usage Card */}
      <Card className={isNearLimit ? "border-amber-200 bg-amber-50/50" : ""}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target className="w-5 h-5" />
              My Daily Usage
            </CardTitle>
            <Badge variant={isNearLimit ? "destructive" : "secondary"}>
              {myUsage.remaining} remaining
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Leads Extracted Today</span>
                <span className="font-heading text-lg font-bold">
                  {myUsage.used} / {myUsage.limit}
                </span>
              </div>
              <Progress value={usagePercent} className={isNearLimit ? "bg-amber-200" : ""} />
            </div>
            
            {isNearLimit && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-100 p-3 rounded-lg">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">
                  You're approaching your daily limit. Consider upgrading for more leads.
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="w-4 h-4" />
              <span>Resets at midnight UTC</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 uppercase tracking-wider font-medium">Current Plan Limit</p>
              <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
                {usage?.plan_limit || 10} leads/day per user
              </p>
              <p className="text-sm text-slate-500 mt-1 capitalize">
                {usage?.plan || "free"} plan
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = "/billing"}>
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Team Usage (for managers) */}
      {teamUsage.length > 0 && (
        <Card data-testid="team-usage">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Usage Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {teamUsage.map((member) => {
                const memberPercent = (member.count / member.limit) * 100;
                const memberNearLimit = memberPercent >= 80;
                
                return (
                  <div key={member.user_id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900">
                        {member.user_name}
                      </span>
                      <span className="text-sm text-slate-600">
                        {member.count} / {member.limit}
                      </span>
                    </div>
                    <Progress 
                      value={memberPercent} 
                      className={memberNearLimit ? "bg-amber-200" : ""}
                    />
                    {member.sources && (
                      <div className="flex gap-2 text-xs text-slate-500">
                        <span>Google Places: {member.sources.google_places || 0}</span>
                        <span>•</span>
                        <span>CSV: {member.sources.csv_import || 0}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
